import { i18n } from "shared";
import { theme } from "shared/src/theme";
import { styled } from "styled-components";
import { useEffect, useState } from "react";
import { useTranslation } from "next-i18next";
import RightIcon from "../../assets/icons/right.svg";
import Head from "next/head";

import { serverSideTranslations } from "next-i18next/serverSideTranslations";

const Wrapper = styled.div`
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 238px;

  & h1,
  h2,
  h3 {
    color: ${theme.gray0};
  }

  & h1 {
    ${theme.typography.headline1}
    margin-bottom: 0px;
  }

  & h2 {
    ${theme.typography.headline2}
    margin-bottom: 16px;
  }
  & h3 {
    ${theme.typography.subtitle1}
  }

  & .top-h3 {
    font-style: italic;
  }

  & h4 {
    ${theme.typography.subtitle2}
    white-space: break-spaces;
    font-weight: 600;
    margin-left: 24px;
    margin-bottom: 0px;
  }

  & p {
    white-space: break-spaces;
    ${theme.typography.body}
  }

  & .info {
    margin-bottom: 64px;
    color: ${theme.black};
  }

  & .description {
    margin-bottom: 0px;
    color: ${theme.gray1};
  }

  & .strategy {
    font-weight: 600;
    font-size: 20px;
    line-height: 32px;
  }

  @media (max-width: 1550px) {
    padding: 0px 5%;
  }
  @media (max-width: 1440px) {
    padding: 0px 9.02% 0px 8.33%;
  }
  @media (max-width: 1245px) {
    padding: 0px 5%;
  }
  @media (max-width: 1150px) {
    padding: 0px 3%;
  }
  @media (max-width: 975px) {
    margin-top: 144px;
    & .top-row {
      flex-direction: column;
    }
    & .top-h3,
    .info {
      margin-bottom: 32px;
    }
  }
  @media (max-width: 768px) {
    padding: 0px 16px;
  }
`;

const Divider = styled.div`
  margin: 16px 0 73px;
  width: 104px;
  height: 3px;
  background-color: ${theme.buttonRed};

  @media (max-width: 768px) {
    margin-bottom: 64px;
  }
`;

const Row = styled.div`
  display: flex;
  margin-bottom: 64px;

  & .content-image,
  .card:first-child {
    margin-right: 24px;
  }

  & .content-image {
    margin-top: 60px;
  }
  & h3 {
    margin-bottom: 64px;
  }

  & .card:hover {
    color: #cb3341;
    & path {
      fill: #cb3341;
    }
  }

  @media (max-width: 1320px) {
    & .content-image {
      height: 465px;
      width: 450px;
    }
  }

  @media (max-width: 975px) {
    & .content-image {
      margin-top: 32px;
      margin-left: 0px;
      height: 376px;
      width: 100%;
      object-fit: contain;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    & .card {
      margin-left: 0px;
      margin-top: 24px;
    }
    .card:first-child {
      margin-right: 0px;
    }
  }
`;

const Card = styled.a`
  max-width: 588px;

  border-radius: 8px;
  border: 1px solid ${theme.gray3};
  padding: 20px 16px 20px 20px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  font-weight: 600;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.0025em;
  color: ${theme.buttonRed};
  min-height: 200px;

  & svg {
    margin-left: 20px;
    min-width: 30px;
    & path {
      fill: ${theme.buttonRed};
    }
  }

  @media (max-width: 885px) {
    flex-direction: column;
    justify-content: center;

    & h4 {
      margin-left: 0px;
      margin-top: 24px;
    }
  }
  @media (max-width: 768px) {
    min-height: 200px;
    max-width: 100%;
  }
  @media (max-width: 475px) {
    align-items: flex-start;
  }
`;

const About = () => {
  const { t } = useTranslation("common");
  const [lang, setLang] = useState("ja");

  useEffect(() => {
    setLang(i18n.language);
  }, [i18n.language]);

  return (
    <>
      <Head>
        <title>
          {t("About")}
          {t("Kansai Startup. Ecosystem - Deep Tech Valley")} {'"KANSAI"'}
        </title>
        <meta name="keywords" content="about" />
        <meta
          key={"about_us"}
          name="description"
          content={t(
            "The Kansai Startup Ecosystem aims to revitalize the Kansai economy by communicating Kansai's attractiveness, potential, and future possibilities both domestically and globally, differentiating Kansai from other areas, and establishing a brand that enables the accumulation of various resources (startups, companies, human resources, investors, venture capital, etc.) both domestically and globally.\n\nIn order to achieve this goal, the 11 prefectures and cities of the Kansai Economic Federation (Shiga, Kyoto, Osaka, Hyogo, Wakayama, Tottori, Tokushima, Kyoto, Osaka, Sakai, and Kobe) have formulated a strategy for disseminating information on the Kansai startup ecosystem."
          )}
        />
      </Head>
      <Wrapper>
        <Row className="top-row">
          <div>
            <h1>{t("About us")}</h1>
            <Divider />
            <h2>{'Deep Tech Valley "KANSAI"'}</h2>
            <h3 className="top-h3">
              {t(
                '"Kansai" has possibilities to create new industries based on it.'
              )}
            </h3>
            <p className="description">
              {t(
                "The Kansai Startup Ecosystem aims to revitalize the Kansai economy by communicating Kansai's attractiveness, potential, and future possibilities both domestically and globally, differentiating Kansai from other areas, and establishing a brand that enables the accumulation of various resources (startups, companies, human resources, investors, venture capital, etc.) both domestically and globally.\n\nIn order to achieve this goal, the 11 prefectures and cities of the Kansai Economic Federation (Shiga, Kyoto, Osaka, Hyogo, Wakayama, Tottori, Tokushima, Kyoto, Osaka, Sakai, and Kobe) have formulated a strategy for disseminating information on the Kansai startup ecosystem."
              )}
            </p>
            <br />
            <br />
            <p className="description">
              {t("In this strategy we have established")}{" "}
            </p>
            <br />
            <br />
            <p className="description strategy">
              {t(
                'Japan\'s Leading R&D Startup Destination Deep Tech Valley "KANSAI"'
              )}
            </p>
            <br />
            <br />
            <p className="description">
              {t(
                "as a brand to be established in the future, and have written down the direction and actions to be taken to disseminate information based on this brand by fiscal 2024.\n\nIn 2024, the final year of the strategy, we have set a KGI (key goal achievement indicator) of ranking in the top 30 of the Global Startup Ecosystem Ranking.\n\nKANSAI, with its high potential for the creation and growth of deep tech, and with the Osaka-Kansai World Exposition coming up in 2025, aims to become a world class startup ecosystem."
              )}
            </p>
          </div>
          <img
            className="content-image"
            src={lang.startsWith("ja") ? "/about-jp.svg" : "/about.svg"}
            alt="About Image"
            width={486}
            height={530}
          />
        </Row>

        <Row>
          <Card as="div" className="logo-div">
            <img src="/logo-lg.svg" alt="challenges in kansai" />
            <h4>
              {t(
                "Imagining Kansai as a Tech Hub.\nStartups, investors, and local governments will collaborate with each other to form a startup ecosystem"
              )}
            </h4>
          </Card>
        </Row>
        <Row>
          <Card
            className="card"
            href={
              lang.startsWith("ja")
                ? "/20211117_information_dissemination_strategy.pdf"
                : "/20211117_information_dissemination_strategy_en.pdf"
            }
            target="_blank"
          >
            <span>
              {t(
                "Click here for details of the information dissemination strategy (PDF)"
              )}
              <RightIcon />
            </span>
          </Card>
          <Card
            className="card"
            href={
              lang.startsWith("ja")
                ? "/KSE_ebook_ja.pdf"
                : "/KSE_ebook_0401_en.pdf"
            }
            target="_blank"
          >
            <span>
              {t("Introduction of KANSAI (PDF)")}
              <RightIcon />
            </span>
          </Card>
        </Row>
      </Wrapper>
    </>
  );
};

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common"])),
    },
  };
}

export default About;
